// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { IncomingCall } from '../Communications/Calls/WebPhone';
import { getLastIncoming, getActiveConnection } from '../../services/voiceDevice';
import './incoming-call-toast.css';

/* Global incoming-call surface.

   Answering used to live inside WebPhone on the Calls page — so once the
   dialpad moved to the Dialer, an inbound call had nowhere to be answered.
   A ringing phone is app-level: it must reach you wherever you are, not only
   on one tab. This mounts once in the app shell and listens to the same
   voiceDevice window events WebPhone already emits. It renders NOTHING until
   a call is actually ringing. */

export default function IncomingCallToast() {
  const [incoming, setIncoming] = useState(null);
  const [active, setActive] = useState(null);

  const sync = useCallback(() => {
    setIncoming(getLastIncoming?.() || null);
    setActive(getActiveConnection?.() || null);
  }, []);

  useEffect(() => {
    const onIncoming = () => sync();
    const onConnect = () => { sync(); };
    const onDisconnect = () => { setIncoming(null); setActive(null); };

    window.addEventListener('voice-incoming', onIncoming);
    window.addEventListener('voice-connect', onConnect);
    window.addEventListener('voice-disconnect', onDisconnect);
    window.addEventListener('voice-error', onDisconnect);
    sync();

    return () => {
      window.removeEventListener('voice-incoming', onIncoming);
      window.removeEventListener('voice-connect', onConnect);
      window.removeEventListener('voice-disconnect', onDisconnect);
      window.removeEventListener('voice-error', onDisconnect);
    };
  }, [sync]);

  // While an inbound call rings: flash the tab title and raise a browser
  // notification so it reaches you with the tab backgrounded. The audible ring
  // is the Voice SDK's incoming sound (enabled at device init). Answer/Decline
  // live on the toast; clicking the notification focuses the tab to reach them.
  useEffect(() => {
    if (!incoming || active) return undefined;
    const fromLabel =
      incoming.parameters?.From ||
      incoming.parameters?.Caller ||
      incoming.parameters?.from ||
      incoming.from ||
      "Incoming call";

    const originalTitle = document.title;
    let on = false;
    const flash = setInterval(() => {
      on = !on;
      document.title = on ? `📞 ${fromLabel}` : originalTitle;
    }, 1000);

    let notif = null;
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        notif = new Notification("Incoming call", {
          body: fromLabel,
          tag: "abon-incoming-call",
          requireInteraction: true,
        });
        notif.onclick = () => {
          try { window.focus(); } catch { /* ignore */ }
          try { notif.close(); } catch { /* ignore */ }
        };
      } else if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch {
      /* notifications unsupported */
    }

    return () => {
      clearInterval(flash);
      document.title = originalTitle;
      try { notif?.close(); } catch { /* ignore */ }
    };
  }, [incoming, active]);

  const accept = useCallback(() => {
    try { incoming?.accept?.(); } catch { /* device handles its own errors */ }
    sync();
  }, [incoming, sync]);

  const reject = useCallback(() => {
    try { incoming?.reject?.(); } catch { /* ignore */ }
    setIncoming(null);
  }, [incoming]);

  // Only the RINGING incoming lives here (Accept / Decline). Once answered, the
  // device fires connect and the global ActiveCall overlay owns the in-call
  // screen — so this is silent during an active call (no duplicate surface).
  if (!incoming || active) return null;

  return (
    <div className="ict" role="alert" aria-live="assertive">
      <IncomingCall call={incoming} accept={accept} reject={reject} />
    </div>
  );
}
